/**
 * «Нормативы урожайности» — справочник культура × уровень (AK-789).
 *
 * Макет (утверждён founder 28.08, «да ок»):
 * `docs/superpowers/prototypes/season-planning-2027/09-yield-norms-screen.html`.
 *
 * Дыра, которую экран закрывает. Норматив урожайности в системе был (AK-762),
 * а входа к нему не было: агроному, у которого «Подтянуть нормативы» в плане
 * пишет «норматив не заведён у культур: нут, лён», идти было НЕКУДА. Теперь
 * маршрут замкнут: завёл здесь → подтянул в плане.
 *
 * Три решения founder по вопросам макета (28.08, «по рекомендациям»):
 *   • уровня кластера НЕТ — только компания и хозяйство;
 *   • норматив на КУЛЬТУРУ, не на сорт;
 *   • экран ЕДИНЫЙ, а не две вкладки: наследование должно быть видно глазом.
 *     Поэтому колонка хозяйства показывает серое «← из компании 26», а не
 *     пустоту: «пусто» и «берёт компанийную» — разные вещи, и различить их
 *     человек обязан без клика.
 *
 * Право на правку — `sowings:U`, то же, что у плановой урожайности посева
 * (граница ручки AK-762). Отдельного «права уровня компании» в RBAC нет:
 * новый ресурс требует досева каталога прав во всех тенантах, и заводить его
 * молча из фиче-ветки нельзя. Пока так — экран об этом говорит подписью
 * «действует на все хозяйства компании», а не притворяется, что различает.
 *
 * Экран НИЧЕГО не считает сам: какой уровень победил — говорит сервер полем
 * `effective` (тот же код, что подставляет цифру полю). Копия лестницы
 * приоритетов на клиенте разошлась бы с серверной на первом исключении.
 */
import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { ArrowRight, Search } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Stack } from '@/components/ui/stack';
import { useCan } from '@/lib/auth/usePermissions';
import { yieldGapsByCrop } from '@/lib/planning/applyYieldDefaults';
import { useVariantFields } from '@/lib/planning/planCrops';
import { usePlanVariants } from '@/lib/planning/techCards';
import { useSeasonByYear } from '@/lib/planning/useSeasonByYear';
import {
  buildYieldNormRows,
  filterYieldNormRows,
  formatCHa,
  useYieldDefaults,
  type YieldNormFilter,
  type YieldNormRow,
} from '@/lib/planning/yieldDefaults';
import { cn } from '@/lib/utils';
import { FarmScopeNotice } from '@/components/scope/FarmScopeNotice';
import { usePlanningFarmScope } from '@/components/scope/usePlanningFarmScope';
import { SeasonNotStartedCard, SeasonYearOutOfWindowCard } from '../SeasonBudgetStates';
import { YieldNormDialog } from './YieldNormDialog';
import { YieldNormsTable } from './YieldNormsTable';

export interface YieldNormsSearch {
  /** Хозяйство экрана — в адресе, чтобы ссылку можно было переслать. */
  farm?: string;
  /** Предвыбранная культура: вход из плана «завести норматив для нута». */
  crop?: string;
  show?: YieldNormFilter;
}

/** Что показывать в таблице. Значения — из адреса, чтобы ссылка вела в то же. */
const SHOW_LABELS: Record<YieldNormFilter, string> = {
  all: 'все культуры',
  plan: 'культуры плана',
  gaps: 'без норматива',
};

/**
 * Выбор культуры — алфавит + поиск (сквозное правило проекта): сверху поиск,
 * ниже полный алфавитный список, без «часто используемых». Рядом с культурой —
 * что у неё сейчас с нормативом, чтобы дыру было видно до выбора.
 */
function CropFilterPicker({
  rows,
  cropId,
  onPick,
}: {
  rows: readonly YieldNormRow[];
  cropId: string | null;
  onPick: (id: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const picked = rows.find((r) => r.cropId === cropId) ?? null;
  const shown = rows.filter((r) =>
    r.cropName.toLowerCase().includes(query.trim().toLowerCase()),
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="justify-between">
          Культура: {picked ? picked.cropName : 'все'}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-2">
        <div className="relative mb-2">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Начните вводить название культуры"
            className="pl-8"
            autoFocus
          />
        </div>
        <ScrollArea className="max-h-64">
          <div className="flex flex-col">
            <button
              type="button"
              className="rounded-md px-2 py-2 text-left text-sm text-foreground hover:bg-accent"
              onClick={() => {
                onPick(null);
                setOpen(false);
              }}
            >
              Все культуры
            </button>
            {shown.map((r) => (
              <button
                key={r.cropId}
                type="button"
                aria-pressed={r.cropId === cropId}
                className={cn(
                  'flex items-center justify-between gap-3 rounded-md px-2 py-2 text-left text-sm',
                  r.cropId === cropId
                    ? 'bg-primary/10 font-medium text-primary'
                    : 'text-foreground hover:bg-accent',
                )}
                onClick={() => {
                  onPick(r.cropId);
                  setOpen(false);
                }}
              >
                <span>{r.cropName}</span>
                <span className="shrink-0 text-xs text-muted-foreground">
                  {r.effectiveCHa !== null
                    ? formatCHa(r.effectiveCHa)
                    : r.planEmptyRows > 0
                      ? `${r.planEmptyRows} без урожайности`
                      : 'норматива нет'}
                </span>
              </button>
            ))}
            {shown.length === 0 && (
              <p className="px-2 py-2 text-sm text-muted-foreground">
                По запросу ничего не нашлось.
              </p>
            )}
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}

export function YieldNormsPage() {
  const params = useParams({ strict: false }) as { year?: string };
  const year = Number(params.year);
  const search = useSearch({ strict: false }) as YieldNormsSearch;
  const navigate = useNavigate();
  const season = useSeasonByYear(year);

  const canRead = useCan('sowings', 'r');
  const canUpdate = useCan('sowings', 'u');
  // Строки плана (сколько полей культуры без урожайности) читает та же ручка,
  // что кормит посевной план, и она под правом на техкарты. Нет права — экран
  // работает как справочник, просто без колонки «дыр».
  const canReadPlan = useCan('tech_cards', 'r');

  // Хозяйство — из переключателя слева (AK-866 В-3: область — из
  // переключателя); `?farm=` — только присланная ссылка (AK-897).
  const farmScope = usePlanningFarmScope({
    searchFarm: search.farm,
    onScopeChanged: () =>
      void navigate({
        to: '/planning/seasons/$year/yield-norms',
        params: { year: String(year) },
        search: {},
        replace: true,
      }),
    onDeepLinkApplied: () =>
      void navigate({
        to: '/planning/seasons/$year/yield-norms',
        params: { year: String(year) },
        search: { ...search, farm: undefined },
        replace: true,
      }),
  });
  const { farmId } = farmScope;
  const farmName = farmScope.farm?.name ?? 'хозяйство';

  const defaults = useYieldDefaults(canRead ? farmId : null, season.seasonId);
  const variants = usePlanVariants(canReadPlan ? farmId : null, season.seasonId);
  const variant =
    (variants.data ?? []).find((v) => v.is_selected) ?? (variants.data ?? [])[0] ?? null;
  const fields = useVariantFields(canReadPlan ? (variant?.id ?? null) : null);

  const planFields = useMemo(() => fields.data ?? [], [fields.data]);
  const gaps = useMemo(() => yieldGapsByCrop(planFields), [planFields]);
  const planCropNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const f of planFields) {
      if (f.plan_crop_id) map.set(f.plan_crop_id, f.plan_crop_name ?? 'культура');
    }
    return map;
  }, [planFields]);

  const rows = useMemo(
    () => buildYieldNormRows({ data: defaults.data, gaps, planCropNames }),
    [defaults.data, gaps, planCropNames],
  );
  const show: YieldNormFilter = search.show ?? 'all';
  const shown = useMemo(
    () => filterYieldNormRows(rows, { show, cropId: search.crop ?? null }),
    [rows, show, search.crop],
  );
  const planRows = rows.filter((r) => r.inPlan).length;
  const gapRows = rows.filter((r) => r.inPlan && r.status === 'none').length;
  const nothingDefined = rows.every((r) => r.status === 'none');

  /** Открытый диалог: уровень + строка (или `null` — заводим с нуля). */
  const [dialog, setDialog] = useState<{
    level: 'farm' | 'company';
    row: YieldNormRow | null;
  } | null>(null);

  const setSearch = (next: Partial<YieldNormsSearch>) =>
    void navigate({
      to: '/planning/seasons/$year/yield-norms',
      params: { year: String(year) },
      search: { ...search, ...next },
    });

  const seasonYears = useMemo(
    () => [...new Set(season.seasons.map((s) => s.year))].sort((a, b) => b - a),
    [season.seasons],
  );

  return (
    // AK-866 В-3: область — из переключателя. Своего выбора хозяйства на
    // экране нет (AK-897).
    <AppShell>
      <div className="mx-auto w-full max-w-[1400px] pb-safe-fab">
        <PageHeader
          title={season.validYear ? `Нормативы урожайности · сезон ${year}` : 'Нормативы урожайности'}
          showBack
          breadcrumbs={[
            { label: 'Планирование сезона', to: '/planning' },
            { label: season.validYear ? `Бюджет сезона ${year}` : 'Бюджет сезона' },
            { label: 'Нормативы урожайности' },
          ]}
          description="Норматив подставляется полю, когда культура заводится без своей цифры. Правка не трогает уже разложенный план."
          actions={
            canUpdate && farmId ? (
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setDialog({ level: 'company', row: null })}
                >
                  По умолчанию для компании
                </Button>
                <Button size="sm" onClick={() => setDialog({ level: 'farm', row: null })}>
                  Задать норматив хозяйству
                </Button>
              </div>
            ) : undefined
          }
        />

        <Stack gap={4} className="mt-4">
          {!season.validYear && (
            <Card>
              <CardContent className="p-4 text-sm text-destructive">
                Год в адресе страницы некорректный.
              </CardContent>
            </Card>
          )}

          {season.validYear && season.isLoading && <Skeleton className="h-96 w-full" />}

          {season.validYear && season.isError && (
            <Card>
              <CardContent className="p-4 text-sm text-destructive">
                Не удалось загрузить сезоны. Обновите страницу.
              </CardContent>
            </Card>
          )}

          {season.validYear && !season.isLoading && !season.seasonId && !season.card && (
            <SeasonYearOutOfWindowCard year={year} />
          )}
          {season.validYear && !season.isLoading && !season.seasonId && !!season.card && (
            <SeasonNotStartedCard year={year} onOpened={() => undefined} />
          )}

          {season.validYear && !canRead && (
            <Card>
              <CardContent className="p-4 text-sm text-muted-foreground">
                Нормативы урожайности вам не открыты: их даёт право на посевы. Попросите
                доступ у администратора хозяйства.
              </CardContent>
            </Card>
          )}

          {season.seasonId && canRead && (
            <>
              <FarmScopeNotice
                scope={farmScope}
                purpose="Своя цифра задаётся по каждому хозяйству отдельно."
              />

              {farmId && (
                <>
                  <Card>
                    <CardContent className="flex flex-wrap items-center gap-2 p-3">
                      {seasonYears.length > 1 && (
                        <Select
                          value={String(year)}
                          onValueChange={(v) =>
                            void navigate({
                              to: '/planning/seasons/$year/yield-norms',
                              params: { year: v },
                              search,
                            })
                          }
                        >
                          <SelectTrigger className="w-auto min-w-[140px]" aria-label="Сезон">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {seasonYears.map((y) => (
                              <SelectItem key={y} value={String(y)}>
                                Сезон {y}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}

                      <CropFilterPicker
                        rows={rows}
                        cropId={search.crop ?? null}
                        onPick={(id) => setSearch({ crop: id ?? undefined })}
                      />

                      <Select
                        value={show}
                        onValueChange={(v) => setSearch({ show: v as YieldNormFilter })}
                      >
                        <SelectTrigger className="w-auto min-w-[200px]" aria-label="Показать">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">{SHOW_LABELS.all}</SelectItem>
                          <SelectItem value="plan">
                            {SHOW_LABELS.plan} ({planRows})
                          </SelectItem>
                          <SelectItem value="gaps">
                            {SHOW_LABELS.gaps} ({gapRows})
                          </SelectItem>
                        </SelectContent>
                      </Select>

                      <span className="text-xs text-muted-foreground">
                        Показано культур: {shown.length} из {rows.length}
                      </span>
                    </CardContent>
                  </Card>

                  {defaults.isLoading && <Skeleton className="h-64 w-full" />}

                  {defaults.isError && (
                    <Card>
                      <CardContent className="p-4 text-sm text-destructive">
                        Не удалось загрузить нормативы урожайности. Обновите страницу.
                      </CardContent>
                    </Card>
                  )}

                  {defaults.data && rows.length === 0 && (
                    <Card>
                      <CardContent className="p-6 text-sm text-muted-foreground">
                        В хозяйстве «{farmName}» ещё нет ни нормативов урожайности, ни
                        разложенного плана. Заведите норматив по культуре — он подставится
                        полям, когда вы начнёте раскладывать культуры.
                      </CardContent>
                    </Card>
                  )}

                  {defaults.data && rows.length > 0 && nothingDefined && (
                    <EmptyNorms
                      farmName={farmName}
                      rows={rows}
                      year={year}
                      canUpdate={canUpdate}
                      onCreate={(level) => setDialog({ level, row: null })}
                    />
                  )}

                  {defaults.data && rows.length > 0 && !nothingDefined && (
                    <Card>
                      <CardContent className="p-0">
                        <YieldNormsTable
                          rows={shown}
                          farmName={farmName}
                          year={year}
                          canUpdate={canUpdate}
                          onEdit={(level, row) => setDialog({ level, row })}
                        />
                      </CardContent>
                    </Card>
                  )}

                  <p className="text-xs text-muted-foreground">
                    «По умолчанию для компании» действует на все хозяйства компании, где нет
                    своей цифры. Норматив — цифра для будущих полей: чтобы подставить её уже
                    разложенным строкам, вернитесь в{' '}
                    <Link
                      to="/planning/seasons/$year/sowing-plan"
                      params={{ year: String(year) }}
                      search={{}}
                      className="font-medium text-primary underline-offset-2 hover:underline"
                    >
                      посевной план
                    </Link>{' '}
                    и нажмите «Подтянуть нормативы урожайности».
                  </p>
                </>
              )}
            </>
          )}
        </Stack>
      </div>

      {dialog && farmId && (
        <YieldNormDialog
          level={dialog.level}
          farmId={farmId}
          farmName={farmName}
          seasonId={season.seasonId}
          seasonYear={year}
          row={dialog.row}
          rows={rows}
          onClose={() => setDialog(null)}
        />
      )}
    </AppShell>
  );
}

/**
 * Пустое состояние (раздел 5 макета): план есть, нормативов нет ни одного.
 *
 * Пустой таблицей экран не бросает: показывает культуры плана, которым цифры
 * не хватает, и явно связывает со вторым шагом — «Подтянуть нормативы» в самом
 * плане. Иначе человек заведёт норматив и будет ждать, что план обновится сам.
 */
function EmptyNorms({
  farmName,
  rows,
  year,
  canUpdate,
  onCreate,
}: {
  farmName: string;
  rows: readonly YieldNormRow[];
  year: number;
  canUpdate: boolean;
  onCreate: (level: 'farm' | 'company') => void;
}) {
  const planRows = rows.filter((r) => r.inPlan);
  return (
    <Card>
      <CardContent className="flex flex-col items-center gap-4 p-6 text-center">
        <div>
          <h2 className="text-base font-semibold text-foreground">
            В хозяйстве «{farmName}» ещё нет ни одного норматива урожайности
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Поля в плане есть, а цифры урожайности им подставлять неоткуда. Заведите
            нормативы по культурам плана — и вернитесь в план подтянуть их в строки.
          </p>
        </div>

        {planRows.length > 0 && (
          <div className="flex flex-wrap justify-center gap-2">
            {planRows.map((r) => (
              <Badge
                key={r.cropId}
                variant="outline"
                className={r.planEmptyRows > 0 ? 'border-destructive text-destructive' : ''}
              >
                {r.cropName}
                {r.planEmptyRows > 0 ? ` · ${r.planEmptyRows} полей без урожайности` : ''}
              </Badge>
            ))}
          </div>
        )}

        {canUpdate && (
          <div className="flex flex-wrap justify-center gap-2">
            <Button variant="outline" onClick={() => onCreate('company')}>
              По умолчанию для компании
            </Button>
            <Button onClick={() => onCreate('farm')}>Задать норматив хозяйству</Button>
          </div>
        )}

        <Link
          to="/planning/seasons/$year/sowing-plan"
          params={{ year: String(year) }}
          search={{}}
          className="inline-flex items-center gap-1 text-sm font-medium text-primary underline-offset-2 hover:underline"
        >
          Посевной план — «Подтянуть нормативы урожайности»
          <ArrowRight className="h-4 w-4" />
        </Link>
      </CardContent>
    </Card>
  );
}

export default YieldNormsPage;
